<template>
  <div>
    <h1 class="page-title">Dashboard</h1>

    <SemEventoBanner v-if="organizadorSemEventos" :nome="authStore.adminName" />

    <div v-if="authStore.isOrganizador && saldos.length" class="saldos-org">
      <div v-for="s in saldos" :key="s.eventoId" class="saldo-org-card card">
        <div class="saldo-topo">
          <span class="saldo-label">Saldo disponível para saque</span>
          <span class="saldo-evento">{{ s.eventoNome }}</span>
        </div>
        <strong class="saldo-valor">{{ formatMoney(s.disponivel) }}</strong>
        <div class="saldo-detalhes">
          <div class="saldo-linha"><span>Recebido</span><span>{{ formatMoney(s.totalRecebido) }}</span></div>
          <div class="saldo-linha muted"><span>Sacado/repassado</span><span>- {{ formatMoney(s.totalRepassado + s.saquesEnviados) }}</span></div>
          <div v-if="s.saquesPendentes > 0" class="saldo-linha muted"><span>Aguardando envio</span><span>- {{ formatMoney(s.saquesPendentes) }}</span></div>
        </div>
        <button v-if="s.disponivel > 0" class="btn-primary btn-solicitar" @click="$router.push('/admin/saques')">Solicitar saque</button>
        <button v-else class="btn-primary btn-solicitar" disabled>Sem saldo disponível</button>
      </div>
    </div>

    <div v-if="loading" class="grid">
      <SkeletonLoader v-for="i in 4" :key="i" variant="card" :count="1" />
    </div>

    <div v-else-if="!organizadorSemEventos" class="grid">
      <div class="stat-card card">
        <span class="stat-label">Eventos ativos</span>
        <strong class="stat-value">{{ totais.eventosAtivos }}</strong>
      </div>
      <div class="stat-card card">
        <span class="stat-label">Inscrições pagas (mês)</span>
        <strong class="stat-value">{{ totais.inscricoesMes }}</strong>
      </div>
      <div v-if="authStore.canSeeFinanceiroCompleto" class="stat-card card">
        <span class="stat-label">Recebido bruto</span>
        <strong class="stat-value">{{ formatMoney(totais.bruto) }}</strong>
      </div>
      <div v-if="authStore.canSeeFinanceiroCompleto" class="stat-card card stat-meu">
        <span class="stat-label">Meu lucro</span>
        <strong class="stat-value">{{ formatMoney(totais.meuLucro) }}</strong>
        <span class="stat-sub">comissões - taxa Asaas</span>
      </div>
      <div v-if="authStore.canSeeFinanceiroCompleto" class="stat-card card stat-repassar">
        <span class="stat-label">A repassar</span>
        <strong class="stat-value">{{ formatMoney(totais.saldoARepassar) }}</strong>
        <span class="stat-sub">soma dos saldos por evento</span>
      </div>
      <div v-if="authStore.canSeeFinanceiroCompleto" class="stat-card card">
        <span class="stat-label">Pendente (entrada)</span>
        <strong class="stat-value">{{ formatMoney(totais.pendente) }}</strong>
      </div>
    </div>

    <div v-if="authStore.canSeeFinanceiroCompleto && resumo.length" class="card stats-evt">
      <h2 class="card-title">Financeiro por evento</h2>
      <div class="table-wrap">
        <table class="table">
          <thead>
            <tr>
              <th>Evento</th>
              <th class="right">Bruto</th>
              <th class="right">Taxa Asaas</th>
              <th class="right" title="Comissão calculada antes do cupom">Comissão</th>
              <th class="right" title="Quanto de comissão você perdeu por cupons">Cupons</th>
              <th class="right">Meu lucro</th>
              <th class="right">A repassar</th>
              <th class="right">Repassado</th>
              <th class="right">Saldo</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in resumo" :key="row.id">
              <td><strong>{{ row.nome }}</strong><br><small>/{{ row.slug }}</small></td>
              <td class="right">{{ formatMoney(row.bruto) }}</td>
              <td class="right muted">- {{ formatMoney(row.taxaAsaas) }}</td>
              <td class="right">{{ formatMoney(row.comissaoBruta) }}</td>
              <td class="right muted" :class="{ destaque: row.descontoPlataformaTotal > 0 }">
                <span v-if="row.descontoPlataformaTotal > 0">- {{ formatMoney(row.descontoPlataformaTotal) }}</span>
                <span v-else>—</span>
              </td>
              <td class="right value-meu">{{ formatMoney(row.meuLucro) }}</td>
              <td class="right">{{ formatMoney(row.aRepassarBruto) }}</td>
              <td class="right muted">- {{ formatMoney(row.totalRepassado) }}</td>
              <td class="right" :class="{ saldo: row.saldoARepassar > 0.01 }">{{ formatMoney(row.saldoARepassar) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="!organizadorSemEventos" class="card recent-card">
      <h2 class="card-title">Eventos recentes</h2>
      <SkeletonLoader v-if="eventosStore.loading" variant="row" :count="3" />
      <div v-else-if="eventosStore.eventos.length" class="table-wrap">
        <table class="table">
          <thead>
            <tr><th>Evento</th><th>Status</th><th>Inscrições</th><th>Planos</th></tr>
          </thead>
          <tbody>
            <tr v-for="e in eventosStore.eventos.slice(0, 8)" :key="e.id" @click="$router.push('/admin/eventos')" class="clickable">
              <td><strong>{{ e.nome }}</strong><br><small>/{{ e.slug }}</small></td>
              <td><StatusChip :status="e.status" kind="evento" /></td>
              <td>{{ e._count?.inscricoes || 0 }}</td>
              <td>{{ e._count?.planos || 0 }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <EmptyState v-else title="Nenhum evento" description="Crie seu primeiro evento." />
    </div>
  </div>
</template>

<script>
import { useAuthStore } from '../../stores/auth'
import { useEventosStore } from '../../stores/eventos'
import api from '../../lib/api'
import SkeletonLoader from '../../components/ui/SkeletonLoader.vue'
import StatusChip from '../../components/ui/StatusChip.vue'
import EmptyState from '../../components/ui/EmptyState.vue'
import SemEventoBanner from '../../components/ui/SemEventoBanner.vue'

export default {
  name: 'DashboardPage',
  components: { SkeletonLoader, StatusChip, EmptyState, SemEventoBanner },
  data() {
    return {
      loading: true,
      resumo: [],
      saldos: [],
      totais: {
        eventosAtivos: 0, inscricoesMes: 0,
        bruto: 0, liquido: 0, taxaAsaas: 0, comissaoPlataforma: 0,
        meuLucro: 0, aRepassarBruto: 0, totalRepassado: 0, saldoARepassar: 0, pendente: 0
      }
    }
  },
  computed: {
    authStore() { return useAuthStore() },
    eventosStore() { return useEventosStore() },
    organizadorSemEventos() {
      return this.authStore.isOrganizador
        && !this.eventosStore.loading
        && this.eventosStore.eventos.length === 0
    }
  },
  async mounted() {
    await this.eventosStore.fetchAll()
    await this.computeStats()
    if (this.authStore.isOrganizador) {
      await this.carregarSaldos()
    }
    this.loading = false
  },
  methods: {
    formatMoney(v) {
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0)
    },
    async carregarSaldos() {
      const eventos = this.eventosStore.eventos.filter(e => e.status !== 'ARQUIVADO')
      const resultados = await Promise.all(eventos.map(async e => {
        try {
          const { data } = await api.get(`/saques/saldo/${e.id}`)
          return { eventoId: e.id, eventoNome: e.nome, ...data }
        } catch { return null }
      }))
      this.saldos = resultados.filter(Boolean).filter(s => s.totalRecebido > 0 || s.disponivel > 0)
    },
    async computeStats() {
      this.totais.eventosAtivos = this.eventosStore.eventos.filter(e => e.status === 'ABERTO').length

      try {
        const { data } = await api.get('/inscricoes', { params: { limit: 200, status: 'QUITADA' } })
        const inicioMes = new Date(); inicioMes.setDate(1); inicioMes.setHours(0, 0, 0, 0)
        const noMes = data.inscricoes.filter(i => new Date(i.createdAt) >= inicioMes)
        this.totais.inscricoesMes = noMes.length
      } catch {}

      if (this.authStore.canSeeFinanceiroCompleto) {
        try {
          const { data } = await api.get('/eventos/resumo')
          const lista = (data.eventos || []).filter(e =>
            (e.bruto || 0) > 0 || (e.pendente || 0) > 0
          )
          this.resumo = lista
          for (const e of lista) {
            this.totais.bruto += e.bruto || 0
            this.totais.liquido += e.liquido || 0
            this.totais.taxaAsaas += e.taxaAsaas || 0
            this.totais.comissaoPlataforma += e.comissaoPlataforma || 0
            this.totais.meuLucro += e.meuLucro || 0
            this.totais.aRepassarBruto += e.aRepassarBruto || 0
            this.totais.totalRepassado += e.totalRepassado || 0
            this.totais.saldoARepassar += e.saldoARepassar || 0
            this.totais.pendente += e.pendente || 0
          }
        } catch {}
      }
    }
  }
}
</script>

<style scoped>
.grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: var(--spacing-base);
  margin-bottom: var(--spacing-lg);
}
@media (max-width: 480px) {
  .grid { grid-template-columns: repeat(2, 1fr); gap: var(--spacing-sm); }
}
.stat-card { padding: var(--spacing-lg); display: flex; flex-direction: column; gap: var(--spacing-xs); }
@media (max-width: 480px) {
  .stat-card { padding: var(--spacing-md); }
  .stat-value { font-size: var(--text-lg); }
}
.stat-label { font-size: var(--text-xs); color: var(--color-text-muted); text-transform: uppercase; letter-spacing: 0.5px; }
.stat-value { font-size: var(--text-2xl); color: var(--color-accent); font-weight: 800; }
.stat-sub { font-size: 11px; color: var(--color-text-muted); }
.stat-meu .stat-value { color: var(--color-success); }
.stat-repassar .stat-value { color: var(--color-warning); }
.stats-evt { padding: var(--spacing-lg); margin-bottom: var(--spacing-lg); }
.table-wrap { overflow-x: auto; }
.recent-card { padding: var(--spacing-lg); }
.card-title { font-size: var(--text-md); margin-bottom: var(--spacing-base); }
.table { width: 100%; border-collapse: collapse; }
.table th {
  text-align: left; padding: 10px var(--spacing-sm);
  font-size: var(--text-xs); color: var(--color-text-muted);
  text-transform: uppercase; letter-spacing: 0.5px;
  border-bottom: 1px solid var(--color-bg-separator);
  white-space: nowrap;
}
.table th.right, .table td.right { text-align: right; }
.table td {
  padding: 10px var(--spacing-sm);
  font-size: var(--text-sm);
  border-bottom: 1px solid var(--color-bg-separator);
  white-space: nowrap;
}
.table tr.clickable { cursor: pointer; }
.table tr.clickable:hover td { background: var(--color-bg-hover); }
.value-meu { color: var(--color-success); font-weight: 600; }
.saldo { color: var(--color-danger); font-weight: 700; }
.destaque { color: var(--color-warning); font-weight: 600; }
.muted, small { color: var(--color-text-muted); }

.saldos-org {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: var(--spacing-base);
  margin-bottom: var(--spacing-lg);
}
.saldo-org-card {
  padding: var(--spacing-lg);
  display: flex; flex-direction: column; gap: var(--spacing-sm);
  border-left: 4px solid var(--color-success);
}
.saldo-topo { display: flex; justify-content: space-between; align-items: baseline; gap: var(--spacing-sm); flex-wrap: wrap; }
.saldo-label {
  font-size: var(--text-xs); color: var(--color-text-muted);
  text-transform: uppercase; letter-spacing: 0.5px;
}
.saldo-evento { font-size: var(--text-sm); color: var(--color-text-muted); }
.saldo-valor {
  font-size: 32px; color: var(--color-success);
  font-weight: 800; line-height: 1.1;
}
.saldo-detalhes {
  display: flex; flex-direction: column; gap: 4px;
  padding-top: var(--spacing-sm);
  border-top: 1px dashed var(--color-bg-separator);
  font-size: var(--text-sm);
}
.saldo-linha { display: flex; justify-content: space-between; }
.saldo-linha.muted { color: var(--color-text-muted); font-size: 12px; }
.btn-solicitar { margin-top: var(--spacing-sm); }
.btn-solicitar:disabled { opacity: 0.5; cursor: not-allowed; }
</style>
